import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { parseSearchTextToSearchParams } from '../../parsers'
import { AccessRight } from '../../types/access-right'
import { InstallationStatus } from 'src/Libs/installation-status'

export type DeviceStatus = 'ok' | 'warning' | 'error' | 'unknown'
export type LastKnownDeviceStatus = Omit<DeviceStatus, 'unknown'>
export type PairingState = 'required' | 'not required' | 'paired'

export interface InstallationDto {
  brandType: string
  city: string
  countryCode: string
  /** ISO date */
  createdAt: string
  createdBy: string
  createdByFirstName: string
  createdByLastName: string
  createdByUsername: string
  deviceStatus: DeviceStatus
  emsMasterDeviceId: string
  emsMasterDeviceShortUuid: string
  emsMasterDeviceType: 'emc' | 'storage'
  emsMasterDeviceUuid: string
  emsMasterId: number
  emsMasterProduct:
    | 'S10 SP40'
    | 'S10 P5002'
    | 'XEM'
    | 'FR2'
    | 'XCM'
    | 'S10 E'
    | 'S10 Mini'
    | 'Quattroporte'
    | 'S10 E PRO'
    | 'S10 E Compact'
    | 'S10 E PRO Compact'
    | 'S10 X/S10 X COMPACT'
    | 'S10 SE'
    | 'S20 X PRO'
  gpsLatitude: number
  gpsLongitude: number
  id: number
  installationRequirements: boolean
  installationStatementId: number
  installationStatus: InstallationStatus
  isActive: boolean
  isFarm: boolean
  lastKnownDeviceStatus: LastKnownDeviceStatus
  lastKnownDeviceStatusTimestamp: string
  ownerAccountId: string
  ownerAccountSource: 'e3dc' | 'hager'
  ownerAccountType: 'user' | 'company' | 'cloud'
  ownerCompanyName: string
  ownerFirstName: string
  ownerId: number
  ownerLastName: string
  ownerUsername: string
  pairingState: string
  projectName: string
  streetName: string
  streetNumber: string
  timezone: string
  /** ISO date */
  updatedAt: string
  updatedBy: string
  zip: string

  /* Permissions, used to disable controls for different installation views */
  permissionType: AccessRight // permission for the whole installation

  storagePermissionType: AccessRight
  endUserPermissionType: AccessRight
  eMobilityPermissionType: AccessRight
}

export const getInstallations = async (
  authFetch: AuthFetchWithResponse,
  limit?: number
): Promise<ApiResult<ArrayType<Nullable<InstallationDto>> | null>> => {
  try {
    const limitParameter = limit !== undefined ? `?limit=${limit}` : ''
    const response = await authFetch<ArrayType<Nullable<InstallationDto>>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations${limitParameter}`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<ArrayType<Nullable<InstallationDto>>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get installations:')
  }
}

export const getInstallation = async (
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<InstallationDto> | null>> => {
  try {
    const response = await authFetch<Nullable<InstallationDto>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<Nullable<InstallationDto>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get installation:')
  }
}

/**
 * Currently not used
 * @deprecated
 */
export const getPaginatedInstallations = async (
  status: InstallationStatus,
  searchPhrase: string,
  limit: number,
  offset: number,
  authFetch: AuthFetchWithResponse,
  sort?: {
    column: string
    direction: 'asc' | 'desc'
  }
): Promise<ApiResult<ArrayType<Nullable<InstallationDto>> | null>> => {
  try {
    const installationFetchParameters = getInstallationFetchParameters(status, searchPhrase)
    const sortParameter = sort ? `&sort=${sort.direction === 'asc' ? '+' : '-'}${sort.column}` : ''

    const response = await authFetch<ArrayType<Nullable<InstallationDto>>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations?${installationFetchParameters}&limit=${limit}&offset=${offset}${sortParameter}`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<ArrayType<Nullable<InstallationDto>>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get paginated installations:')
  }
}

export const getInstallationsCount = async (
  status: InstallationStatus,
  searchPhrase: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<{ count: number } | null>> => {
  try {
    const installationFetchParameters = getInstallationFetchParameters(status, searchPhrase)

    const response = await authFetch<{ count: number }>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/stats?${installationFetchParameters}`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<{ count: number }>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get installations count:')
  }
}

/** Parses the single search text and status into parameters for multiple fields on the BE. */
const getInstallationFetchParameters = (status: InstallationStatus, searchPhrase: string): string => {
  const { filter, filterOr, searchPhrase: parsedSearchPhrase } = parseSearchTextToSearchParams(searchPhrase, status)

  const searchParameter = parsedSearchPhrase ? `&searchPhrase=${parsedSearchPhrase}` : ''
  const filterOrParameter = filterOr ? `&filterOr=${filterOr}` : ''

  return `filter=${filter}${searchParameter}${filterOrParameter}`
}

export type InstallationSynchronizePOSTorPUTDto = {
  needToBeSynchronize: boolean
}

export type InstallationPOSTorPUTDto = InstallationSynchronizePOSTorPUTDto &
  Pick<InstallationDto, 'projectName' | 'countryCode' | 'timezone' | 'streetName' | 'streetNumber' | 'city' | 'zip'> & {
    gpsLatitude: string
    gpsLongitude: string
  }

const prepareInstallationPayload = (
  installation: InstallationPOSTorPUTDto | InstallationSynchronizePOSTorPUTDto
): InstallationPOSTorPUTDto | Omit<InstallationPOSTorPUTDto | InstallationSynchronizePOSTorPUTDto, 'gpsLatitude' | 'gpsLongitude'> => {
  if (!('gpsLatitude' in installation) || !('gpsLongitude' in installation)) {
    return installation
  }

  const { gpsLatitude, gpsLongitude, ...other } = installation
  if (!gpsLatitude?.trim() || !gpsLongitude?.trim()) {
    return other
  }
  return installation
}

export const createInstallation = async (
  installation: InstallationPOSTorPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number | null>> => {
  try {
    const response = await authFetch<number>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: 'installations',
      method: 'POST',
      payload: prepareInstallationPayload(installation),
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<number>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to create installation:')
  }
}

export const updateInstallation = async (
  installationId: number,
  installation: InstallationPOSTorPUTDto | InstallationSynchronizePOSTorPUTDto,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}`,
      method: 'PUT',
      payload: prepareInstallationPayload(installation),
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update installation:')
  }
}

export const deleteInstallation = async (installationId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}`,
      method: 'DELETE',
    })

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to delete installation:')
  }
}
